import type { PiIconName } from '@uiKit/elements/PiIcon.vue'

/**
 * Quick-pick amounts offered under the amount field in both money-moving
 * modals. Fixed AED values per design — the withdraw modal disables any
 * preset above the investor's available balance rather than hiding it, so the
 * row keeps its shape while staying honest about what can actually be moved.
 */
// eslint-disable-next-line no-magic-numbers -- AED preset amounts are the canonical product values
export const AMOUNT_PRESETS = [1000, 2000, 5000, 10000] as const

/** Default amount the Add Funds modal opens on. */
export const DEFAULT_DEPOSIT_AMOUNT = 5000

/** Default amount the Withdraw modal opens on, clamped to available balance. */
export const DEFAULT_WITHDRAWAL_AMOUNT = 2000

/** Step applied by the withdraw amount field's up/down stepper. */
export const AMOUNT_STEP = 1000

export type FundingMethodId = 'linked_bank' | 'card' | 'digital_wallet' | 'bank_transfer'

export interface FundingMethod {
  id: FundingMethodId
  title: string
  subtitle: string
  /** Right-hand pill: fee / speed for live methods, availability for the rest. */
  tag: string
  icon: PiIconName
  /**
   * Only `bank_transfer` is wired to an endpoint today — `/wallets/fiat/deposits`
   * takes a wire reference plus a receipt. Card, digital wallet and linked-bank
   * pull have no backend, so they render disabled with a "Coming soon" pill
   * instead of a fee promise the platform can't honour on a money screen.
   */
  available: boolean
}

export const FUNDING_METHODS: readonly FundingMethod[] = [
  {
    id: 'bank_transfer',
    title: 'Bank Transfer',
    subtitle: 'Wire instructions provided',
    tag: '1-2 Days',
    icon: 'building-columns',
    available: true,
  },
  {
    id: 'linked_bank',
    title: 'Linked Bank Account',
    subtitle: 'Pay straight from your saved bank',
    tag: 'Coming soon',
    icon: 'building',
    available: false,
  },
  {
    id: 'card',
    title: 'Debit / Credit Card',
    subtitle: 'Instant funding to balance',
    tag: 'Coming soon',
    icon: 'credit-card',
    available: false,
  },
  {
    id: 'digital_wallet',
    title: 'Digital Wallet',
    subtitle: 'Apple Pay / Google Pay',
    tag: 'Coming soon',
    icon: 'phone',
    available: false,
  },
] as const

/** Copy shown under the withdraw summary — sets the arrival expectation. */
export const WITHDRAWAL_ARRIVAL_NOTE =
  'Your withdrawal will be transferred to your bank account within 72 hours.'
